import {AgentsNotSupportedDialog, NoAgentsAvailableDialog} from '@github-ui/copilot-chat/components/AgentsDialogs'
import {KnowledgeSelectPanel} from '@github-ui/copilot-chat/components/KnowledgeSelectPanel'
import {ReferencesSelectPanel} from '@github-ui/copilot-chat/components/ReferencesSelectPanel'
import {useChatState} from '@github-ui/copilot-chat/CopilotChatContext'
import {useInsertAgent, type UseInsertAgentProps} from '@github-ui/copilot-chat/utils/agents-helpers'
import {isRepository, makeImageReference} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'
import {sendEvent} from '@github-ui/hydro-analytics'
import {BookIcon, FileCodeIcon, ImageIcon, RepoIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {type RefObject, useRef} from 'react'

import {useChatAutocomplete} from '../utils/CopilotChatAutocompleteContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {isCustomCopilot} from '../utils/custom-copilots-helpers'
import {AgentMenu} from './AgentMenu'
import type {AttachmentMenuPanelType} from './AttachmentMenuPanelType'
import {ExtensionsMenu} from './ExtensionsMenu'
import {COPILOT_CHAT_MENU_PORTAL_ROOT} from './PortalContainerUtils'
import {RepoSelectPanel} from './RepoSelectPanel'

interface AttachmentMenuProps extends UseInsertAgentProps {
  panel: AttachmentMenuPanelType | null
  onPanelChange: React.Dispatch<React.SetStateAction<AttachmentMenuPanelType | null>>
  anchorRef: RefObject<HTMLButtonElement>
}

/**
 * Should be limited to images that we know we can base64 encode and are supported by our models.
 */
const ACCEPTED_IMAGE_FILE_EXTENSIONS: string[] = ['.gif', '.jpg', '.jpeg', '.png']

export function AttachmentMenu({panel, onPanelChange: setPanel, anchorRef, ...props}: AttachmentMenuProps) {
  const {inputRef} = props
  const manager = useChatManager()
  const state = useChatState()
  const autocomplete = useChatAutocomplete()

  const referencesEnabled = isRepository(state.currentTopic)
  const shouldShowKnowledge =
    state.renderKnowledgeBases && ((state.currentTopic && isRepository(state.currentTopic)) || !state.currentTopic)

  // For now, we are focused on building out image support in immersive mode only.
  const shouldShowImages = copilotFeatureFlags.attachImagesImmersive && state.mode === 'immersive'

  const insertAgent = useInsertAgent(props)
  const customCopilotsEnabled = copilotFeatureFlags.customCopilots
  const folderReferencesEnabled = copilotFeatureFlags.attachFolderReference

  const addImageReference = (name: string, image: string) => {
    // TODO - use upcoming ambient error if more than one image is attached
    if (!shouldShowImages) return // don't want to accidentally send CAPI references it doesn't understand
    const imageRef = makeImageReference(name, image)
    void autocomplete.addToReferences(imageRef, state)
  }

  const imagesInputRef = useRef<HTMLInputElement>(null)

  return (
    <div>
      {shouldShowImages && (
        <input
          id="image-uploader"
          hidden
          ref={imagesInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_FILE_EXTENSIONS.join(', ')}
          onChange={e => {
            if (!e.target.files) return
            const file = e.target.files[0]
            if (file) {
              // if a user selects the same file two times in a row, still fire the event
              e.currentTarget.value = ''
              const reader = new FileReader()

              // error here if there is already one image uploaded
              const hasImageReference = state.currentReferences.some(ref => ref.type === 'image')
              if (hasImageReference) {
                manager.addAmbientError('Only one image can be uploaded at a time')
                return
              }

              reader.onload = function (loadedFileEvent) {
                // This executes asynchronously after the call to readAsDataURL.
                if (!loadedFileEvent.target || !loadedFileEvent.target.result) return
                const result = loadedFileEvent.target.result
                if (typeof result !== 'string') return
                const base64String = result.split(',')[1] // Strip the data URL prefix
                if (base64String) {
                  if (base64String.length > 500_000) {
                    manager.addAmbientError('Image is over the 500KB limit after base64 conversion')
                    return
                  }

                  addImageReference(file.name, result)
                }
              }

              reader.onerror = function (fileErrorEvent) {
                // This executes asynchronously after the call to readAsDataURL.
                if (!fileErrorEvent.target) return
                const error = fileErrorEvent.target.error
                if (error) {
                  // since this is kicking off as a staff ship, it should be safe to expose the error name and message
                  manager.addAmbientError(`An error occurred while reading the file: ${error.name} - ${error.message}`)
                }
              }

              reader.readAsDataURL(file) // Reads the file as a data URL, which includes the Base64-encoded string
            }
          }}
        />
      )}
      <ActionMenu
        anchorRef={anchorRef}
        open={panel === 'attachment-types'}
        onOpenChange={newOpen =>
          setPanel(currentOpen => {
            // The menu should only be able to close the menu, not the other panel types. Otherwise it will autoclose
            // the dialogs as soon as they open as it tries to close itself.
            if (newOpen && currentOpen === null) return 'attachment-types'
            if (!newOpen && currentOpen === 'attachment-types') return null
            return currentOpen
          })
        }
      >
        <ActionMenu.Overlay
          width="small"
          side="outside-top"
          align="end"
          portalContainerName={state.mode === 'assistive' ? COPILOT_CHAT_MENU_PORTAL_ROOT : undefined}
        >
          <ActionList>
            <ActionList.Item
              onSelect={() => {
                sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_REFERENCES', mode: state.mode})
                setPanel('references')
              }}
              inactiveText={referencesEnabled ? undefined : 'First attach a repository'}
            >
              <ActionList.LeadingVisual>
                <FileCodeIcon />
              </ActionList.LeadingVisual>
              {folderReferencesEnabled ? <>Files, folders, and symbols&hellip;</> : <>Files and symbols&hellip;</>}
            </ActionList.Item>
            <ActionList.Item
              onSelect={() => {
                sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_REPOSITORIES', mode: state.mode})
                setPanel('repositories')
              }}
            >
              <ActionList.LeadingVisual>
                <RepoIcon />
              </ActionList.LeadingVisual>
              Repository&hellip;
            </ActionList.Item>
            {shouldShowKnowledge && (
              <ActionList.Item
                onSelect={() => {
                  sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_KNOWLEDGE_BASES', mode: state.mode})
                  setPanel('knowledge-bases')
                }}
              >
                <ActionList.LeadingVisual>
                  <BookIcon />
                </ActionList.LeadingVisual>
                Knowledge base&hellip;
              </ActionList.Item>
            )}
            {shouldShowImages && (
              <ActionList.Item
                onSelect={() => {
                  sendEvent('dotcom_chat.activate', {target: 'ATTACHMENT_MENU_IMAGES', mode: state.mode})
                  imagesInputRef.current?.click()
                }}
              >
                <ActionList.LeadingVisual>
                  <ImageIcon />
                </ActionList.LeadingVisual>
                Image&hellip;
              </ActionList.Item>
            )}
            {customCopilotsEnabled ? (
              <ExtensionsMenu
                onSelectPanel={value => setPanel(value)}
                // setTimeout required to pull focus after ActionMenu tries to return to anchor
                onSelectExtension={extension =>
                  setTimeout(() => insertAgent(isCustomCopilot(extension) ? extension.slugWithOwner : extension.slug))
                }
              />
            ) : (
              <AgentMenu
                onSelectPanel={value => setPanel(value)}
                // setTimeout required to pull focus after ActionMenu tries to return to anchor
                onSelectAgent={agent => setTimeout(() => insertAgent(agent.slug))}
              />
            )}
          </ActionList>
        </ActionMenu.Overlay>
      </ActionMenu>

      <ReferencesSelectPanel
        open={panel === 'references'}
        onOpenChange={val => setPanel(val ? 'references' : null)}
        submitReturnFocusRef={inputRef}
        cancelReturnFocusRef={anchorRef}
      />

      <RepoSelectPanel
        open={panel === 'repositories'}
        onOpenChange={isOpen => setPanel(isOpen ? 'repositories' : null)}
        submitReturnFocusRef={inputRef}
        cancelReturnFocusRef={anchorRef}
      />

      <KnowledgeSelectPanel
        disabledForPreviewModelsProps={{anchorRef}}
        emptyKnowledgeBaseProps={{anchorRef}}
        open={panel === 'knowledge-bases'}
        onOpenChange={val => setPanel(val ? 'knowledge-bases' : null)}
        submitReturnFocusRef={inputRef}
        cancelReturnFocusRef={anchorRef}
      />

      {panel === 'no-agents-available' && <NoAgentsAvailableDialog onClose={() => setPanel(null)} />}
      {panel === 'agents-not-supported' && <AgentsNotSupportedDialog onClose={() => setPanel(null)} />}
    </div>
  )
}

try{ AttachmentMenu.displayName ||= 'AttachmentMenu' } catch {}